import {InputElementType, Validation, type ValidationConfig, type ValidationResponse, WarningLevel} from "./validations";
import type {InputType} from "../commons";
import type {FormInputElement} from "../components/formInputElement";
import {LocalDate} from "../../../common/utils/dates/localDate";
import {dateExpressionFrom} from "../../../common/utils/dates/parseDate";

export type DateRangeConfig = ValidationConfig & {
    id: "dateRange";
    startDate?: string;
    endDate?: string;
};

export class DateRange extends Validation {

    private constructor(
        messageText: string,
        warningLevel: WarningLevel,
        private startDate?: string,
        private endDate?: string
    ) {
        super(messageText, warningLevel);
    }

    public static from(config: DateRangeConfig): DateRange {
        return new DateRange(config.message, config.warningLevel, config.startDate, config.endDate);
    }

    protected valid(value: unknown): boolean {
        if (value === "") {
            return true;
        }

        const dateFromExpression = LocalDate.fromDateExpression(value as string);
        if (!dateFromExpression.isValid()) {
            return false;
        }
        if (this.endDate && LocalDate.fromDateExpression(this.endDate).isBefore(dateFromExpression)) {
            return false;
        }
        if (this.startDate && LocalDate.fromDateExpression(this.startDate).isAfter(dateFromExpression)) {
            return false;
        }
        return true;
    }

    public accept<T extends InputType>(inputElement: FormInputElement<T>, value: T): void {
        inputElement.setType(InputElementType.DATE);
        if (this.endDate) {
            inputElement.addCustomAttribute("max", this.toDateAttribute(this.endDate));
        }
        if (this.startDate) {
            inputElement.addCustomAttribute("min", this.toDateAttribute(this.startDate));
        }

        const validationResponse: ValidationResponse = this.checkValidity(value);

        if (!validationResponse.valid) {
            inputElement.addMessage({warningLevel: validationResponse.warningLevel, message: this.errorText});
        }
    }

    private toDateAttribute(relativeOrAbsoluteDate: string): string {
        return dateExpressionFrom(relativeOrAbsoluteDate).toLocalDate().toHtml5DateString();
    }
}